import { useTranslation } from "react-i18next";
import { getAuthRegistry } from "@/lib/auth/AuthRegistry";

const SIGNUP_URL = "https://id.boona.app/signup";

/**
 * The demo's only exit toward signing up.
 *
 * Measured 2026-08-28: 14% of paid clicks reach the demo and 0% reach a signup page.
 * `admin.boona.app` had never emitted a single click event, and there was no visible route
 * from the product back to signup — the most engaged paid visitor on record spent ~10
 * minutes inside the demo, returned to the marketing page and left. This is that missing
 * route. See boona docs/SEO/ads/analytics-journeys.md.
 *
 * Renders for demo sessions only, so a paying customer never sees it.
 */
export default function DemoBanner() {
  const { t } = useTranslation();
  if (!getAuthRegistry().isDemo()) return null;

  // /demo sets boona_locale on .boona.app with httponly=False precisely so the client can
  // read it, so the signup page opens in the language the visitor was browsing in.
  const locale = document.cookie
    .split("; ")
    .find((c) => c.startsWith("boona_locale="))
    ?.slice("boona_locale=".length);
  const href = locale
    ? `${SIGNUP_URL}?lang=${encodeURIComponent(decodeURIComponent(locale))}`
    : SIGNUP_URL;

  return (
    <div
      data-testid="demo-banner"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-blue-200 bg-blue-50/95 backdrop-blur
                 px-4 py-3 shadow-lg"
      role="region"
      aria-label={t("demo.banner.aria", "Demo mode")}
    >
      <div className="mx-auto flex max-w-4xl flex-col items-center gap-2 text-center
                      sm:flex-row sm:justify-between sm:text-left">
        <p className="text-sm text-blue-900">
          <span className="font-semibold">
            {t("demo.banner.title", "You're exploring a demo.")}
          </span>{" "}
          <span className="text-blue-800">
            {t("demo.banner.subtitle",
               "Sample data, nothing you change is saved. Your own salon takes a minute to set up.")}
          </span>
        </p>
        <a
          data-testid="demo-signup-cta"
          href={href}
          className="shrink-0 rounded-lg bg-blue-600 px-5 py-2 text-sm font-medium text-white
                     transition-colors hover:bg-blue-700"
        >
          {t("demo.banner.cta", "Create my account")}
        </a>
      </div>
    </div>
  );
}
